import { useMemo, useState } from 'react'

import { fieldHint, fieldLabel, groupLabel, useQuoteI18n } from '../quoteI18n'

/**
 * Form dei dati che il trip Goelett richiede prima di poter essere prenotato.
 *
 * I campi non sono fissi: `GetRequiredBookingData` dice per ogni trip cosa serve
 * (passaporto, documento, telefono, campi analitici, approvatore) e l'Executor pubblica
 * il descrittore sul preventivo. Qui si rende quello, raggruppato per sezione.
 *
 * @param {Object} props
 * @param {{fields: Array}} props.form - Descrittore del form pubblicato dall'Executor
 * @param {boolean} props.submitting - True mentre l'invio è in corso
 * @param {string} [props.error] - Errore da mostrare sopra al pulsante
 * @param {(values: Object) => void} props.onSubmit - Invio dei valori compilati
 */
export function BookingDataForm({ form, submitting, error, onSubmit }) {
  const { t } = useQuoteI18n()
  const [values, setValues] = useState({})
  const [touched, setTouched] = useState(false)

  // `form?.fields || []` produrrebbe un array nuovo a ogni render, rifacendo il
  // raggruppamento anche quando il form non è cambiato.
  const fields = useMemo(() => form?.fields || [], [form])

  // Le sezioni seguono l'ordine in cui l'Executor ha emesso i campi: raggruppare senza
  // riordinare tiene insieme i campi di uno stesso documento.
  const groups = useMemo(() => {
    const byGroup = new Map()
    for (const field of fields) {
      // Si raggruppa sul codice della sezione, non sul titolo: il titolo cambia con la
      // lingua e riordinerebbe il form a ogni cambio.
      const key = field.groupKey || field.group
      if (!byGroup.has(key)) byGroup.set(key, [])
      byGroup.get(key).push(field)
    }
    return [...byGroup.entries()]
  }, [fields])

  const missing = fields.filter((field) => field.required && !hasValue(field, values[field.key]))
  const requiredCount = fields.filter((field) => field.required).length

  const handleChange = (field, rawValue) => {
    setValues((current) => ({ ...current, [field.key]: rawValue }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setTouched(true)
    if (missing.length || submitting) return
    onSubmit(values)
  }

  if (!fields.length) return null

  return (
    <form className="gq-form" onSubmit={handleSubmit}>
      <div className="gq-form__intro">
        <span className="gq-form__intro-icon" aria-hidden="true">
          📋
        </span>
        <div>
          <p className="gq-form__intro-title">{t.form.introTitle}</p>
          <p className="gq-form__intro-text">{t.form.introText(requiredCount)}</p>
        </div>
      </div>

      {groups.map(([group, groupFields]) => (
        <fieldset className="gq-fieldset" key={group}>
          <legend className="gq-fieldset__legend">{groupLabel(t, groupFields[0])}</legend>
          {groupFields.map((field) => (
            <FormField
              key={field.key}
              field={field}
              value={values[field.key]}
              invalid={touched && field.required && !hasValue(field, values[field.key])}
              onChange={(rawValue) => handleChange(field, rawValue)}
            />
          ))}
        </fieldset>
      ))}

      {touched && missing.length > 0 && (
        <p role="alert" className="gq-error">
          {t.form.missing(missing.map((field) => fieldLabel(t, field)).join(', '))}
        </p>
      )}
      {error && (
        <p role="alert" className="gq-error">
          {error}
        </p>
      )}

      <button type="submit" className="gq-btn gq-btn--primary gq-btn--block" disabled={submitting}>
        {submitting ? t.form.submitting : t.form.submit}
      </button>
    </form>
  )
}

/** Un singolo campo, reso in base al `type` del descrittore. */
function FormField({ field, value, invalid, onChange }) {
  const { t } = useQuoteI18n()
  const label = fieldLabel(t, field)
  const hint = fieldHint(t, field)

  if (field.type === 'checkbox') {
    return (
      <label className="gq-check">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          {label}
          {field.required && <span className="gq-field__required">*</span>}
        </span>
      </label>
    )
  }

  return (
    <div className="gq-field">
      <label className="gq-field__label" htmlFor={field.key}>
        {label}
        {field.required && <span className="gq-field__required">*</span>}
      </label>
      {field.type === 'select' ? (
        <select
          className="gq-field__input"
          id={field.key}
          value={value || ''}
          aria-invalid={invalid}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{t.form.selectPlaceholder}</option>
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="gq-field__input"
          id={field.key}
          type={field.type}
          value={value || ''}
          aria-invalid={invalid}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {hint && <p className="gq-field__hint">{hint}</p>}
    </div>
  )
}

/** Un campo è compilato se spuntato (checkbox) o non vuoto (tutti gli altri). */
function hasValue(field, value) {
  return field.type === 'checkbox' ? value === true : !!String(value ?? '').trim()
}
